<template>
    <div class="no-print mb-5">
        <label :for="inputId" class="default-label"> Language </label>

        <v-select
            v-model="selectedLanguage"
            data-cy="language-selector"
            :items="localeOptions"
            :list-props="{ density: 'compact' }"
            :menu-props="{
                contentClass: 'language-dropdown-menu',
            }"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            item-color="black"
            @update:modelValue="onSelectValueChange"
        >
            <template #selection="{ internalItem: item }">
                <span data-cy="language-code" class="font-weight-small">
                    {{ languageNames[item.raw.code.toLowerCase()] }}
                </span>
            </template>

            <template #item="{ props }">
                <v-list-item v-bind="props" class="px-4" />
            </template>
        </v-select>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref, useId } from "vue";
import { useSessionStore } from "@/plugins/store";

const { availableLocales } = useI18n();
const sessionStore = useSessionStore();
const inputId = useId();

const resolveLocale = (lang?: string | null) => {
    if (!lang) return undefined;
    if (availableLocales.includes(lang)) return lang;
    const primary = lang.split("-")[0];
    return availableLocales.find((loc) => loc.split("-")[0] === primary);
};

const defaultLocale =
    resolveLocale(sessionStore.language) ??
    resolveLocale("en") ??
    availableLocales[0];

const selectedLanguage = ref(defaultLocale);

const emit = defineEmits(["update:modelValue"]);

const languageNames: Record<string, string> = {
    en: "English",
    fr: "Français",
    de: "Deutsch",
};

const localeOptions = computed(() =>
    availableLocales.map((loc) => {
        const langCode = loc.split("-")[0];

        return {
            value: loc,
            title: languageNames[langCode] || loc,
            code: langCode.toUpperCase(),
        };
    }),
);

const onSelectValueChange = (language: string) => {
    emit("update:modelValue", language);
};
</script>
